import { useEffect, useRef, useState } from "react";
import { Bold, Code2, Italic, List, ListOrdered, Type, Underline } from "lucide-react";
import { htmlToMd, looksMarkdown, mdToHtml, sanitizeHtml } from "@/lib/markdown";

const BUTTONS = [
  { key: "bold", cmd: "bold", icon: Bold, label: "Bold" },
  { key: "italic", cmd: "italic", icon: Italic, label: "Italic" },
  { key: "underline", cmd: "underline", icon: Underline, label: "Underline" },
  { key: "bullets", cmd: "insertUnorderedList", icon: List, label: "Bullets" },
  { key: "numbered", cmd: "insertOrderedList", icon: ListOrdered, label: "Numbered" },
  { key: "heading", cmd: "formatBlock", arg: "H3", icon: Type, label: "Heading" },
];

/**
 * One text box for the whole diary: type and it formats live (no markdown symbols on screen),
 * with a source view for anyone who wants the raw markdown. Pasted markdown is formatted too.
 * `mode="markdown"` stores markdown, `mode="html"` stores HTML (hearing logs).
 */
export const MarkdownField = ({
  value,
  onChange,
  mode = "markdown",
  testId = "markdown-field",
  minHeight = 120,
  maxHeight = 320,
  placeholder = "",
  disabled = false,
  bare = false,
}) => {
  const ref = useRef(null);
  const mine = useRef(null);
  const [source, setSource] = useState(false);
  const [draft, setDraft] = useState("");

  const toHtml = (v) => (mode === "html" ? sanitizeHtml(v) : mdToHtml(v));
  const fromHtml = (h) => (mode === "html" ? sanitizeHtml(h) : htmlToMd(h));

  useEffect(() => {
    if (source || !ref.current) return;
    if ((value || "") === (mine.current || "")) return;
    ref.current.innerHTML = toHtml(value);
    mine.current = value || "";
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, source, mode]);

  const emit = () => {
    const next = fromHtml(ref.current?.innerHTML || "");
    mine.current = next;
    onChange(next);
  };

  const exec = (cmd, arg) => {
    if (disabled) return;
    ref.current?.focus();
    document.execCommand(cmd, false, arg || null);
    emit();
  };

  const toggleSource = () => {
    if (source) {
      mine.current = null; // force the formatted view to repaint from `value`
      setSource(false);
      return;
    }
    setDraft(mode === "html" ? htmlToMd(value) : value || "");
    setSource(true);
  };

  const editSource = (raw) => {
    setDraft(raw);
    const next = mode === "html" ? mdToHtml(raw) : raw;
    mine.current = next;
    onChange(next);
  };

  // markdown pasted from notes or a message should arrive formatted, not as asterisks
  const paste = (e) => {
    if (disabled || e.clipboardData?.getData("text/html")) return;
    const text = e.clipboardData?.getData("text/plain") || "";
    if (!looksMarkdown(text)) return;
    e.preventDefault();
    document.execCommand("insertHTML", false, mdToHtml(text));
    emit();
  };

  const empty = !(value || "").trim();

  return (
    <div
      className={
        bare
          ? "rounded-sm overflow-hidden"
          : "border border-input rounded-sm overflow-hidden bg-card"
      }
      data-testid={`${testId}-wrap`}
    >
      <div
        className={`flex items-center gap-0.5 px-1.5 py-1 ${
          bare ? "border-b border-current/20" : "border-b border-border bg-secondary/60"
        }`}
      >
        {BUTTONS.map(({ key, cmd, arg, icon: Icon, label }) => (
          <button
            key={key}
            type="button"
            title={label}
            disabled={disabled}
            data-testid={`${testId}-${key}`}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => exec(cmd, arg)}
            className="h-7 w-7 grid place-items-center rounded-sm opacity-70 hover:opacity-100 hover:bg-accent/60 transition-opacity duration-200"
          >
            <Icon className="h-3.5 w-3.5" />
          </button>
        ))}
        <button
          type="button"
          data-testid={`${testId}-source-toggle`}
          onClick={toggleSource}
          className="ml-auto inline-flex items-center gap-1 h-7 px-2 rounded-sm text-[0.68rem] font-semibold uppercase tracking-wider opacity-70 hover:opacity-100 hover:bg-accent/60 transition-opacity duration-200"
          title={source ? "Back to the formatted view" : "See the markdown"}
        >
          <Code2 className="h-3.5 w-3.5" /> {source ? "Formatted" : "Markdown"}
        </button>
      </div>

      {source ? (
        <textarea
          data-testid={`${testId}-source`}
          value={draft}
          disabled={disabled}
          onChange={(e) => editSource(e.target.value)}
          style={{ minHeight, maxHeight }}
          className="w-full px-3 py-2.5 text-sm font-mono bg-transparent outline-none resize-y overflow-y-auto"
        />
      ) : (
        <div className="relative">
          {empty && placeholder ? (
            <span className="pointer-events-none absolute left-3 top-2.5 text-sm opacity-50 whitespace-pre-line">
              {placeholder}
            </span>
          ) : null}
          <div
            ref={ref}
            data-testid={testId}
            contentEditable={!disabled}
            suppressContentEditableWarning
            onInput={emit}
            onBlur={emit}
            onPaste={paste}
            style={{ minHeight, maxHeight, resize: "vertical" }}
            className="rich-text px-3 py-2.5 text-sm leading-relaxed outline-none focus:ring-2 focus:ring-ring/30 overflow-y-auto"
          />
        </div>
      )}
    </div>
  );
};
